import React, { useMemo, useState } from "react";
import { IMemorization } from "../../models/memorization";
import { CalculatePageProgress } from "./surah-pages";
import { CalculateAyahProgress } from "./surah-ayat";
import { IMemorizationChapter } from "../../models/memorization-chapter";
import { IActualAyatPctProgressTimeline, IAyahProgress, IChartPoint, ILatestMemorizationPoint, IMemorizationStats, IPageProgress } from "./models/dtos";
import { MemorizationProgressGraf } from "./memorization-progress-graf.";
import { MemorizationProgressStat } from "./memorization-progress-stat";


function addYears(date: Date, years: number) {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
}

function formatDate(date: Date) {
  return date.toLocaleDateString("da-DK", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function daysBetween(a: Date, b: Date) {
  return Math.max( 0, Math.ceil((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24)));
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}


function completedeMemorizations(memorizations: IMemorization[]) {
  return memorizations
    .filter((m) => m.status === 2 && m.completedDate)
    .sort(
      (a, b) =>
        new Date(a.completedDate).getTime() -
        new Date(b.completedDate).getTime()
    );
}

function plannedPct(startDate: Date, plannedFinishDate: Date, axisDate: Date) {
    const totalPlannedDays = Math.max(1,daysBetween(startDate, plannedFinishDate));

    const plannedPct = clamp((daysBetween(startDate, axisDate) / totalPlannedDays) * 100, 0, 100);

    return Number(plannedPct.toFixed(2));
}

function actualAyatPct(actualAyatPctProgressTimeline: IActualAyatPctProgressTimeline[], xAxisDate: Date) {
  let actualAyatPct: number | null = null;
  for (const point of actualAyatPctProgressTimeline) {
    if (point.date.getTime() <= xAxisDate.getTime()) {
      actualAyatPct = point.actualAyatPct;
    } else {
      break;
    }
  }
  return actualAyatPct !== null ? Number(actualAyatPct.toFixed(2)) : null;
}

function forecastAyatPct(currentAyatPct: number, lastMemorizationCompletedDate: Date, forecastFinishDate: Date | null, xAxisDate: Date) {
    let forecastAyatPct: number | null = null;
    
    if (forecastFinishDate && xAxisDate >= lastMemorizationCompletedDate) {
      
      const totalForecastDays = Math.max(1, daysBetween(lastMemorizationCompletedDate, forecastFinishDate));
      
      const elapsedForecastDays = daysBetween(lastMemorizationCompletedDate, xAxisDate);
      
      forecastAyatPct = clamp(
        currentAyatPct + ((100 - currentAyatPct) * elapsedForecastDays) / totalForecastDays,
        currentAyatPct,
        100
      );
    }
    return forecastAyatPct !== null ? Number(forecastAyatPct.toFixed(2)) : null;
}

function latestPointFromMemorizations(chapter: IMemorizationChapter, ayahProgress: IAyahProgress, pageProgress: IPageProgress) {
   const latestMemorizationPoint = {
      surahName: chapter.name,
      chapterNumber: chapter.chapterNumber,
      ayah: chapter.toAyah,
      displayText: `${chapter.name} (${chapter.chapterNumber}:${chapter.toAyah})`,
      ayatProgressPct: ayahProgress.progressPct,
      ayatRemainingPct: ayahProgress.remainingPct,
      memorizedAyat: ayahProgress.memorizedAyat,
      remainingAyat: ayahProgress.remainingAyat,
      pageProgressPct: pageProgress.progressPct,
      pageRemainingPct: pageProgress.remainingPct,
      memorizedPages: pageProgress.memorizedPages,
      remainingPages: pageProgress.remainingPages,
  };

  return latestMemorizationPoint;
}

function buildChartData(memorizations: IMemorization[], customStartDate?: Date | null) {
  
  const completedMemorizations = completedeMemorizations(memorizations);

  if (!completedMemorizations.length) {
    return {
      chartData: [] as IChartPoint[],
      stats: null,
    };
  }

  const studentYears = parseFloat(String(completedMemorizations[0]?.student?.years ?? "5").replace(",", "."));
  
  const startDate = customStartDate ?? new Date(completedMemorizations[0].createdAt);
  
  const lastMemorizationCompletedDate = new Date(
    completedMemorizations[completedMemorizations.length - 1].completedDate
  );

  const plannedFinishDate = addYears(startDate, studentYears);

  const actualAyatPctProgressTimeline: IActualAyatPctProgressTimeline[] = [];

  let latestMemorizationPoint: ILatestMemorizationPoint | null = null;

  for (const completedMemorization of completedMemorizations) {
    const newChapters = completedMemorization.memorizationChapters?.filter((c) => c.memorizationType === 1)?.sort((a, b) => a.chapterNumber - b.chapterNumber);

    if (!newChapters?.length) continue;

    const chapter = newChapters[0];

    const ayahProgress = CalculateAyahProgress(chapter.chapterNumber, chapter.toAyah);

    const pageProgress = CalculatePageProgress(chapter.chapterNumber, chapter.toAyah);

    actualAyatPctProgressTimeline.push({
      date: new Date(completedMemorization.completedDate),
      actualAyatPct: ayahProgress.progressPct,
    });

    latestMemorizationPoint = latestPointFromMemorizations(chapter, ayahProgress, pageProgress);
  }

  if (!latestMemorizationPoint || !actualAyatPctProgressTimeline.length) {
    return {
      chartData: [] as IChartPoint[],
      stats: null,
    };
  }

  const currentAyatPct = latestMemorizationPoint.ayatProgressPct;
  const elapsedDays = Math.max(1, daysBetween(startDate, lastMemorizationCompletedDate));
  const pctPerDay = currentAyatPct / elapsedDays;

  let forecastFinishDate: Date | null = null;
  if (pctPerDay > 0) {
    const remainingDays = Math.ceil((100 - currentAyatPct) / pctPerDay);
    forecastFinishDate = new Date(lastMemorizationCompletedDate);
    forecastFinishDate.setDate(forecastFinishDate.getDate() + remainingDays);
  }

  const chartEndDate = new Date(
    Math.max(
      plannedFinishDate.getTime(),
      lastMemorizationCompletedDate.getTime(),
      (forecastFinishDate ?? lastMemorizationCompletedDate).getTime()
    )
  );

  const chartData: IChartPoint[] = [];
  const xAxisDate = new Date(startDate);

  while (xAxisDate <= chartEndDate) {
    
    chartData.push({
      date: formatDate(new Date(xAxisDate)),
      planned: plannedPct(startDate, plannedFinishDate, xAxisDate),
      actualAyatPct: actualAyatPct(actualAyatPctProgressTimeline, xAxisDate),
      forecastAyatPct: forecastAyatPct(currentAyatPct, lastMemorizationCompletedDate, forecastFinishDate, xAxisDate),
    });

    xAxisDate.setDate(xAxisDate.getDate() + 14);
  }

  const plannedPctToday = clamp(
    (daysBetween(startDate, lastMemorizationCompletedDate) / Math.max(1, daysBetween(startDate, plannedFinishDate))) * 100,
     0,
     100
  );

  const measuredDays = Math.max(1, elapsedDays);
  const ayatPerDay = Number((latestMemorizationPoint.memorizedAyat / measuredDays).toFixed(2));
  const pagesPerDay = Number((latestMemorizationPoint.memorizedPages / measuredDays).toFixed(2));
  const remainingDaysToPlannedFinish = daysBetween(lastMemorizationCompletedDate, plannedFinishDate);

  const requiredAyatPerDayToFinishOnPlan = remainingDaysToPlannedFinish > 0
    ? Number((latestMemorizationPoint.remainingAyat / remainingDaysToPlannedFinish).toFixed(2))
    : null;

  const requiredPagesPerDayToFinishOnPlan = remainingDaysToPlannedFinish > 0
    ? Number((latestMemorizationPoint.remainingPages / remainingDaysToPlannedFinish).toFixed(2))
    : null;

  return {
    chartData,
    stats: {
      plannedFinishDate: formatDate(plannedFinishDate),
      forecastFinishDate: forecastFinishDate ? formatDate(forecastFinishDate) : "Ukendt",
      plannedPctToday: Number(plannedPctToday.toFixed(2)),
      behindPct: Number((plannedPctToday - currentAyatPct).toFixed(2)),
      ayatPerDay,
      pagesPerDay,
      measuredDays,
      remainingDaysToPlannedFinish,
      requiredAyatPerDayToFinishOnPlan,
      requiredPagesPerDayToFinishOnPlan,
      latestMemorizationPoint: latestMemorizationPoint,
    } as IMemorizationStats | null,
  };
}

export type MemorizationOverviewProps = {
    memorizations: IMemorization[];
}

export function MemorizationProgress( props: MemorizationOverviewProps) {

   const {memorizations} = props;

  const [startDateInput, setStartDateInput] = useState("");
  const [activeTab, setActiveTab] = useState<"graf" | "stat">("graf");
  const [showStartDateHelp, setShowStartDateHelp] = useState(false);

  const customStartDate = startDateInput ? new Date(`${startDateInput}T00:00:00`) : null;

  const { chartData, stats } = useMemo(
    () => buildChartData(memorizations, customStartDate),
    [memorizations, startDateInput]
  );

  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginLeft: "12px", marginBottom: "10px", maxWidth: "320px" }}>
        <div
          style={{ display: "inline-flex", alignItems: "center", gap: "6px", marginBottom: "4px", position: "relative" }}
          onMouseEnter={() => setShowStartDateHelp(true)}
          onMouseLeave={() => setShowStartDateHelp(false)}
        >
          <label
            htmlFor="custom-start-date"
            style={{ display: "block", fontSize: "13px", cursor: "default", marginBottom: 0 }}
          >
            Valgfri startdato
          </label>
          <span
            style={{
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              border: "1px solid #197770",
              color: "#197770",
              fontSize: "11px",
              fontWeight: 700,
              lineHeight: "14px",
              textAlign: "center",
              cursor: "help",
              userSelect: "none",
            }}
            aria-label="Info om valgfri startdato"
            title="Hold musen over for mere info"
          >
            i
          </span>

          {showStartDateHelp && (
            <div
              style={{
                position: "absolute",
                top: "24px",
                left: 0,
                width: "300px",
                fontSize: "13px",
                lineHeight: 1.45,
                color: "#374151",
                backgroundColor: "#f0f9f7",
                border: "1px solid #ccece7",
                borderRadius: "8px",
                padding: "8px 10px",
                zIndex: 10,
                boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
              }}
            >
              Hvis du vælger en valgfri startdato, beregnes planlagt og forventet fremdrift ud fra den dato i stedet for elevens oprindelige startdato. Den oprindelige startdato er baseret pa den første memorering, der blev registreret i appen.
            </div>
          )}
        </div>

        <input
          id="custom-start-date"
          type="date"
          className="form-control"
          value={startDateInput}
          onChange={(e) => setStartDateInput(e.target.value)}
        />
      </div>

      <div style={{ display: "flex", gap: "8px", marginBottom: "12px", marginLeft: "12px" }}>
        <button
          type="button"
          className="btn"
          style={activeTab === "graf"
            ? { backgroundColor: "#197770", borderColor: "#197770", color: "#fff" }
            : { backgroundColor: "transparent", borderColor: "#197770", color: "#197770" }}
          onClick={() => setActiveTab("graf")}
        >
          Graf
        </button>
        <button
          type="button"
          className="btn"
          style={activeTab === "stat"
            ? { backgroundColor: "#197770", borderColor: "#197770", color: "#fff" }
            : { backgroundColor: "transparent", borderColor: "#197770", color: "#197770" }}
          onClick={() => setActiveTab("stat")}
        >
          Statistik
        </button>
      </div>

      {activeTab === "graf" && (
        <MemorizationProgressGraf chartData={chartData}></MemorizationProgressGraf>
      )}
      {activeTab === "stat" && (
        <MemorizationProgressStat memorizationStats={stats}></MemorizationProgressStat>
      )}
    </div>
  );
}